import * as T from 'three';

// shared the scene shapes and materials
export function createSceneKit() {
  const materials = new Map<string, T.Material>();
  const geometries = new Map<string, T.BufferGeometry>();
  const textures = new Set<T.Texture>();
  const spriteMaterials = new Set<T.Material>();

  // reuse each colour and shape
  const mat = (color: string, flat = false) => {
    const key = `${color}${flat ? '!' : ''}`;
    if (!materials.has(key))
      materials.set(
        key,
        flat
          ? new T.MeshBasicMaterial({ color })
          : new T.MeshStandardMaterial({ color, roughness: 0.82, flatShading: true }),
      );
    return materials.get(key)!;
  };
  const geo = (key: string, create: () => T.BufferGeometry) => {
    if (!geometries.has(key)) geometries.set(key, create());
    return geometries.get(key)!;
  };

  // place the shared meshes
  const mesh = (
    geometry: T.BufferGeometry,
    color: string,
    x: number,
    y: number,
    z: number,
    parent: T.Object3D,
    rotation = 0,
  ) => {
    const object = new T.Mesh(geometry, mat(color));
    object.position.set(x, y, z);
    object.rotation.y = rotation;
    object.castShadow = true;
    object.receiveShadow = true;
    parent.add(object);
    return object;
  };
  const box = (
    w: number,
    h: number,
    d: number,
    color: string,
    x: number,
    y: number,
    z: number,
    parent: T.Object3D,
    rotation = 0,
  ) =>
    mesh(
      geo(`b${w},${h},${d}`, () => new T.BoxGeometry(w, h, d)),
      color,
      x,
      y,
      z,
      parent,
      rotation,
    );
  const sphere = (r: number, color: string, x: number, y: number, z: number, parent: T.Object3D) =>
    mesh(
      geo(`s${r}`, () => new T.SphereGeometry(r, 7, 5)),
      color,
      x,
      y,
      z,
      parent,
    );
  const cylinder = (
    r: number,
    h: number,
    color: string,
    x: number,
    y: number,
    z: number,
    parent: T.Object3D,
    top = r,
    segments = 10,
  ) =>
    mesh(
      geo(`c${r},${h},${top},${segments}`, () => new T.CylinderGeometry(top, r, h, segments)),
      color,
      x,
      y,
      z,
      parent,
    );
  const cone = (
    r: number,
    h: number,
    color: string,
    x: number,
    y: number,
    z: number,
    parent: T.Object3D,
    segments = 4,
    rotation = Math.PI / 4,
  ) =>
    mesh(
      geo(`k${r},${h},${segments}`, () => new T.ConeGeometry(r, h, segments)),
      color,
      x,
      y,
      z,
      parent,
      rotation,
    );
  const dome = (r: number, color: string, x: number, y: number, z: number, parent: T.Object3D) =>
    mesh(
      geo(`d${r}`, () => new T.SphereGeometry(r, 14, 7, 0, Math.PI * 2, 0, Math.PI / 2)),
      color,
      x,
      y,
      z,
      parent,
    );

  // draw flat artwork onto a ground or wall plane
  const picture = (
    canvas: HTMLCanvasElement,
    w: number,
    h: number,
    x: number,
    y: number,
    z: number,
    parent: T.Object3D,
    rotationX = -Math.PI / 2,
    rotationY = 0,
  ) => {
    const texture = new T.CanvasTexture(canvas);
    texture.colorSpace = T.SRGBColorSpace;
    textures.add(texture);
    const material = new T.MeshBasicMaterial({ map: texture });
    spriteMaterials.add(material);
    const object = new T.Mesh(
      geo(`p${w},${h}`, () => new T.PlaneGeometry(w, h)),
      material,
    );
    object.position.set(x, y, z);
    object.rotation.set(rotationX, rotationY, 0, 'YXZ');
    parent.add(object);
    return object;
  };

  // reuse the sign drawing
  const draw = (value: string, color: string, background: string | null, letterSpacing = 0) => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;
    const context = canvas.getContext('2d')!;
    if (background) {
      context.fillStyle = background;
      context.fillRect(0, 0, 512, 128);
    }
    context.font = `700 ${letterSpacing ? 46 : 44}px "Plus Jakarta Sans", Arial`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    (context as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = `${letterSpacing}px`;
    context.fillStyle = color;
    context.fillText(value, 256, 68, 490);
    return canvas;
  };
  const text = (
    value: string,
    x: number,
    y: number,
    z: number,
    parent: T.Object3D,
    width = 5,
    color = '#162e3a',
    background: string | null = '#fffdf5',
  ) => {
    const texture = new T.CanvasTexture(draw(value, color, background));
    textures.add(texture);
    const material = new T.SpriteMaterial({ map: texture, depthTest: false });
    spriteMaterials.add(material);
    const sprite = new T.Sprite(material);
    sprite.position.set(x, y, z);
    sprite.scale.set(width, width / 4, 1);
    parent.add(sprite);
    return sprite;
  };
  // lay district names flat on the ground like the reference map
  const label = (value: string, x: number, z: number, parent: T.Object3D, width = 20, color = '#6b7f8a') => {
    const canvas = draw(value, color, null, 6);
    const object = picture(canvas, width, width / 4, x, 0.3, z, parent);
    (object.material as T.MeshBasicMaterial).transparent = true;
    return object;
  };

  // a flat sign on a wall (a venue's name), facing +z unless turned
  const sign = (
    value: string,
    x: number,
    y: number,
    z: number,
    parent: T.Object3D,
    width = 2.4,
    color = '#fffdf5',
    background = '#1d4ed8',
    rotationY = 0,
  ) => picture(draw(value, color, background, 2), width, width / 4, x, y, z, parent, 0, rotationY);

  // a floor drawn in a pattern of one colour: square tiles in two tones (with thin grout), long boards, big
  // plain slabs, stone flags, carpet or mown grass; `tile` is the pattern's size in metres
  const tiled = (
    w: number,
    d: number,
    color: string,
    x: number,
    y: number,
    z: number,
    parent: T.Object3D,
    tile = 0.6,
    contrast = 0.07,
    style: 'tiles' | 'boards' | 'plain' | 'stone' | 'carpet' | 'grass' = 'tiles',
  ) => {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 128;
    const context = canvas.getContext('2d')!;
    const base = new T.Color(color);
    const tone = (by: number) => '#' + base.clone().offsetHSL(0, 0, by).getHexString();
    const line = (from: [number, number], to: [number, number]) => {
      context.beginPath();
      context.moveTo(...from);
      context.lineTo(...to);
      context.stroke();
    };
    context.strokeStyle = '#' + base.clone().multiplyScalar(0.82).getHexString();
    context.lineWidth = 2;
    let repeat = tile * 2;
    if (style === 'boards') {
      // planks across the room, their ends staggered
      for (let i = 0; i < 8; i += 1) {
        context.fillStyle = tone([0, 0.03, -0.02, 0.015][i % 4]!);
        context.fillRect(0, i * 16, 128, 16);
        line([0, i * 16], [128, i * 16]);
        const end = (i * 53) % 128;
        line([end, i * 16], [end, i * 16 + 16]);
      }
      repeat = 2.4;
    } else if (style === 'grass') {
      // mowing stripes
      for (let i = 0; i < 2; i += 1) {
        context.fillStyle = tone(i ? 0.025 : -0.01);
        context.fillRect(i * 64, 0, 64, 128);
      }
      repeat = 2.4;
    } else if (style === 'carpet' || style === 'plain') {
      context.fillStyle = tone(0);
      context.fillRect(0, 0, 128, 128);
      for (let i = 0; i < (style === 'carpet' ? 900 : 0); i += 1) {
        context.fillStyle = tone(((i * 7) % 5) / 100 - 0.02);
        context.fillRect((i * 37) % 128, (i * 91) % 128, 2, 2);
      }
      if (style === 'plain') {
        line([1, 0], [1, 128]);
        line([0, 1], [128, 1]);
      }
      repeat = style === 'plain' ? 3 : 1.5;
    } else if (style === 'stone') {
      // flags of different sizes
      const flags: [number, number, number, number][] = [
        [0, 0, 70, 50],
        [70, 0, 58, 50],
        [0, 50, 45, 78],
        [45, 50, 83, 40],
        [45, 90, 83, 38],
      ];
      flags.forEach(([fx, fy, fw, fh], i) => {
        context.fillStyle = tone([0, 0.04, -0.03, 0.02, -0.015][i]!);
        context.fillRect(fx, fy, fw, fh);
        context.strokeRect(fx + 1, fy + 1, fw - 2, fh - 2);
      });
      repeat = 2.2;
    } else {
      const tones = [tone(contrast), tone(0)];
      for (let row = 0; row < 2; row += 1)
        for (let column = 0; column < 2; column += 1) {
          context.fillStyle = tones[(row + column) % 2]!;
          context.fillRect(column * 64, row * 64, 64, 64);
        }
      for (const at of [1, 64, 127]) {
        line([at, 0], [at, 128]);
        line([0, at], [128, at]);
      }
    }
    const texture = new T.CanvasTexture(canvas);
    texture.colorSpace = T.SRGBColorSpace;
    texture.wrapS = texture.wrapT = T.RepeatWrapping;
    texture.repeat.set(w / repeat, d / repeat);
    texture.anisotropy = 4;
    textures.add(texture);
    const material = new T.MeshStandardMaterial({ map: texture, roughness: style === 'tiles' ? 0.6 : 0.85 });
    spriteMaterials.add(material);
    const object = new T.Mesh(
      geo(`f${w},${d}`, () => new T.PlaneGeometry(w, d)),
      material,
    );
    object.rotation.x = -Math.PI / 2;
    object.position.set(x, y, z);
    object.receiveShadow = true;
    parent.add(object);
    return object;
  };

  // the ground round a room, fading into the sky at its edge
  const ground = (radius: number, color: string, parent: T.Object3D) => {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 256;
    const context = canvas.getContext('2d')!;
    const fill = new T.Color(color);
    const rgb = `${Math.round(fill.r * 255)},${Math.round(fill.g * 255)},${Math.round(fill.b * 255)}`;
    const gradient = context.createRadialGradient(128, 128, 40, 128, 128, 128);
    gradient.addColorStop(0, `rgba(${rgb},1)`);
    gradient.addColorStop(0.72, `rgba(${rgb},0.92)`);
    gradient.addColorStop(1, `rgba(${rgb},0)`);
    context.fillStyle = gradient;
    context.fillRect(0, 0, 256, 256);
    const texture = new T.CanvasTexture(canvas);
    texture.colorSpace = T.SRGBColorSpace;
    textures.add(texture);
    // unlit: it lies outside the room, and a lit see-through surface this big costs too much on phones
    const material = new T.MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false });
    spriteMaterials.add(material);
    const object = new T.Mesh(
      geo(`g${radius}`, () => new T.CircleGeometry(radius, 48)),
      material,
    );
    object.rotation.x = -Math.PI / 2;
    object.position.y = -0.21;
    parent.add(object);
    return object;
  };

  // something that gives light (a lamp's bulb): it glows whatever the light on it
  const glow = (r: number, color: string, x: number, y: number, z: number, parent: T.Object3D) => {
    const key = `glow${color}`;
    if (!materials.has(key))
      materials.set(key, new T.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1.6 }));
    const object = new T.Mesh(
      geo(`s${r}`, () => new T.SphereGeometry(r, 10, 8)),
      materials.get(key)!,
    );
    object.position.set(x, y, z);
    parent.add(object);
    return object;
  };

  // a box that gives light (a screen, neon, a lit sign): bright whatever the light on it
  const lit = (
    w: number,
    h: number,
    d: number,
    color: string,
    x: number,
    y: number,
    z: number,
    parent: T.Object3D,
    strength = 1.2,
  ) => {
    const key = `lit${color}${strength}`;
    if (!materials.has(key))
      materials.set(key, new T.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: strength }));
    const object = new T.Mesh(
      geo(`b${w},${h},${d}`, () => new T.BoxGeometry(w, h, d)),
      materials.get(key)!,
    );
    object.position.set(x, y, z);
    parent.add(object);
    return object;
  };

  // see-through glass (a meeting room, a shop window, a display case)
  const glass = (
    w: number,
    h: number,
    d: number,
    x: number,
    y: number,
    z: number,
    parent: T.Object3D,
    tint = '#cfe8f2',
  ) => {
    const key = `glass${tint}`;
    if (!materials.has(key))
      materials.set(
        key,
        new T.MeshStandardMaterial({
          color: tint,
          transparent: true,
          opacity: 0.32,
          roughness: 0.1,
          depthWrite: false,
        }),
      );
    const object = new T.Mesh(
      geo(`b${w},${h},${d}`, () => new T.BoxGeometry(w, h, d)),
      materials.get(key)!,
    );
    object.position.set(x, y, z);
    parent.add(object);
    return object;
  };

  // materials made for this scene elsewhere (a painted model) and work to stop when it goes (a model loading)
  const cancels = new Set<() => void>();
  const own = (material: T.Material) => void spriteMaterials.add(material);
  const onDispose = (cancel: () => void) => void cancels.add(cancel);

  // release only this scene's resources
  const dispose = () => {
    cancels.forEach((cancel) => cancel());
    geometries.forEach((geometry) => geometry.dispose());
    materials.forEach((material) => material.dispose());
    textures.forEach((texture) => texture.dispose());
    spriteMaterials.forEach((material) => material.dispose());
  };
  return {
    mesh,
    box,
    sphere,
    cylinder,
    cone,
    dome,
    picture,
    text,
    label,
    sign,
    tiled,
    ground,
    glow,
    lit,
    glass,
    mat,
    geo,
    own,
    onDispose,
    dispose,
  };
}
export type SceneKit = ReturnType<typeof createSceneKit>;
